import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowClockwise, CheckCircle, DownloadSimple, SignOut, Package, Ruler,
  Play, Wrench, Eye, User
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MyTasksBanner from "@/components/MyTasksBanner";
import { JobWorkBadge } from "@/components/SheetBilling";
import { HeyShobhit } from "@/components/assistant/HeyShobhit";

export default function OperatorPanel() {
  const { user, logout } = useAuth();
  const [queue, setQueue] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [aiOn, setAiOn] = useState(false);
  const isMd = useMemo(() => window.matchMedia("(min-width: 768px)").matches, []);
  useEffect(() => { api.get("/assistant/access").then(({ data }) => setAiOn(!!data.enabled)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const [{ data: q }, { data: ops }, { data: dd }] = await Promise.all([
        api.get("/orders/queue"),
        api.get("/analytics/operators"),
        api.get("/analytics/operator/daily?days=14"),
      ]);
      setQueue(q);
      const mine = (ops?.operators || []).find((o) => o.name === user?.name);
      setMyStats(mine || { orders_all_time: 0, sqft_all_time: 0, orders_this_month: 0, sqft_this_month: 0 });
      setDaily(dd);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Gate/railing cutting is a single "design_cutting" state (no explicit
  // in-progress split), so we surface it in the READY lane so the operator
  // sees & can act on it. Once they mark it "completed" it drops off queue.
  const ready = useMemo(
    () => queue.filter((o) => o.status === "ready_to_cut" || o.status === "design_cutting"),
    [queue]
  );
  const inCutting = useMemo(() => queue.filter((o) => o.status === "in_cutting"), [queue]);

  const markDone = async (id, orderNumber) => {
    setBusyId(id);
    try {
      await api.post(`/orders/${id}/status`, { status: "completed" });
      toast.success(`#${orderNumber} cutting done — ready for delivery 🚚`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const download = async (o) => {
    const tok = getAuthToken() || "";
    window.open(`${API}/orders/${o.id}/download/cutting_file?auth=${encodeURIComponent(tok)}`, "_blank");
    // Backend auto-advances ready_to_cut OR design_cutting (gate flow) → in_cutting
    // when the DXF is downloaded. Refresh after a short delay so the card
    // hops from "Ready to Cut" lane to "In Cutting" lane immediately.
    if (o.status === "ready_to_cut" || o.status === "design_cutting") {
      toast.success(`Starting cutting for #${o.order_number}…`);
      setTimeout(load, 900);
    }
  };

  const previewDesign = (o) => {
    if (!o.design_preview) {
      toast.info("Is order me design abhi upload nahi hui hai.");
      return;
    }
    // Use the SAME public endpoint that the customer sees on their WhatsApp
    // approval link — no auth needed, browser will render the actual image
    // (jpg/png/webp) inline. This guarantees operator sees exactly what the
    // customer received; no chance of accidentally serving cutting_file/DXF.
    window.open(
      `${API}/public/track/${o.order_number}/design-preview`,
      "_blank",
    );
  };

  return (
    <div className="min-h-screen bp-grid">
      <header className="border-b-2 border-orange-500/60 bg-slate-900/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Shobhit Creation"
              className="h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
            <div>
              <div className="font-heading text-lg md:text-xl font-black tracking-wide text-white leading-none">
                CUTTING QUEUE
              </div>
              <div className="text-[11px] font-mono-num text-orange-400 tracking-widest mt-1">
                // OPERATOR TERMINAL · {user?.name?.toUpperCase()}
              </div>
            </div>
          </div>
          {aiOn && isMd && <div className="flex-1 max-w-md" data-testid="operator-hey-shobhit"><HeyShobhit /></div>}
          <div className="ml-auto flex items-center gap-3">
            <Button
              onClick={load}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-800 rounded-sm h-11 px-4"
              data-testid="refresh-queue-button"
            >
              <ArrowClockwise size={18} className="mr-1" /> Refresh
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-800 rounded-sm h-11"
              data-testid="operator-logout-button"
            >
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </header>
      {aiOn && !isMd && <div className="px-4 pt-3" data-testid="operator-hey-shobhit-mobile"><HeyShobhit /></div>}

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        <MyTasksBanner includeUnassigned className="mb-4" />
        {/* My stats banner */}
        {myStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="operator-stats">
            <StatBox label="Orders (This Month)" value={myStats.orders_this_month} tone="orange" />
            <StatBox label="SqFt (This Month)" value={myStats.sqft_this_month} tone="cyan" />
            <StatBox label="Orders (All Time)" value={myStats.orders_all_time} tone="emerald" />
            <StatBox label="SqFt (All Time)" value={myStats.sqft_all_time} tone="emerald" />
          </div>
        )}

        {/* Day-wise cutting chart */}
        {daily?.series?.length > 0 && (
          <DailyCuttingChart series={daily.series} scope={daily.scope} />
        )}

        {loading ? (
          <div className="text-slate-500 font-mono-num text-center py-20">Loading queue…</div>
        ) : (
          <div className="space-y-8">
            {/* Lane 1: Ready to Cut */}
            <section data-testid="lane-ready-to-cut">
              <LaneHeader
                accent="orange"
                title="READY TO CUT"
                count={ready.length}
                subtitle="Download the DXF to start cutting. Order moves to IN CUTTING automatically."
              />
              {ready.length === 0 ? (
                <EmptyLane message="No jobs waiting to start." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                  {ready.map((o) => (
                    <ReadyCard
                      key={o.id}
                      o={o}
                      onDownload={() => download(o)}
                      onPreview={() => previewDesign(o)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Lane 2: In Cutting */}
            <section data-testid="lane-in-cutting">
              <LaneHeader
                accent="cyan"
                title="IN CUTTING"
                count={inCutting.length}
                subtitle="Currently on the machine. Mark done once cutting is complete."
              />
              {inCutting.length === 0 ? (
                <EmptyLane message="Nothing on the machine right now." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                  {inCutting.map((o) => (
                    <InCuttingCard
                      key={o.id}
                      o={o}
                      busy={busyId === o.id}
                      onDownload={() => download(o)}
                      onMarkDone={() => markDone(o.id, o.order_number)}
                      onPreview={() => previewDesign(o)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

/* ---------------- Lane primitives ---------------- */
function LaneHeader({ title, count, subtitle, accent = "orange" }) {
  const tone = accent === "orange"
    ? { bar: "bg-orange-500", text: "text-orange-400", chip: "border-orange-500/50 text-orange-400" }
    : { bar: "bg-cyan-400", text: "text-cyan-300", chip: "border-cyan-500/50 text-cyan-300" };
  return (
    <div className="flex items-center gap-4">
      <div className={`w-1.5 h-10 ${tone.bar}`} />
      <div className="flex-1">
        <div className={`font-mono-num text-[11px] tracking-widest ${tone.text}`}>// {title}</div>
        <div className="font-heading text-2xl md:text-3xl font-black text-white">
          {count} <span className="text-slate-500 text-lg font-normal">
            {accent === "orange" ? "waiting" : "on machine"}
          </span>
        </div>
        <div className="text-xs text-slate-400 mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

function EmptyLane({ message }) {
  return (
    <div className="surface p-8 rounded-sm text-center mt-4 border border-dashed border-slate-800">
      <div className="text-slate-500 text-sm">{message}</div>
    </div>
  );
}

/* ---------------- Ready-to-cut card ---------------- */
function ReadyCard({ o, onDownload, onPreview }) {
  const emergency = !!o.is_emergency;
  const hasDesign = !!o.design_preview;
  return (
    <div
      className={
        "surface p-6 md:p-7 rounded-sm border-l-4 relative " +
        (emergency
          ? "border-red-500 ring-2 ring-red-500/50 shadow-lg shadow-red-500/20 bg-red-500/5"
          : "border-orange-500")
      }
      data-testid={`operator-card-${o.order_number}`}
    >
      {emergency && <EmergencyPill />}
      {o.billing_mode === "job_work" && <div className="absolute -top-3 left-4 z-10"><JobWorkBadge small testid={`operator-job-work-${o.order_number}`} /></div>}
      <CardHeader o={o} label="READY" tone={emergency ? "red" : "orange"} />
      <ItemsGrid o={o} />
      <div className="flex flex-col gap-3">
        {hasDesign && (
          <Button
            onClick={onPreview}
            variant="outline"
            className="w-full h-12 rounded-sm border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 font-heading tracking-widest"
            data-testid={`preview-design-${o.order_number}`}
          >
            <Eye size={18} weight="bold" className="mr-2" />
            PREVIEW DESIGN
          </Button>
        )}
        <Button
          onClick={onDownload}
          disabled={!o.cutting_file}
          className={
            "w-full h-20 rounded-sm text-white font-heading text-xl md:text-2xl tracking-widest font-black transition-colors " +
            (emergency
              ? "bg-red-500 hover:bg-red-400 active:bg-red-600"
              : "bg-orange-500 hover:bg-orange-400 active:bg-orange-600")
          }
          data-testid={`download-cutting-${o.order_number}`}
        >
          <Play size={26} weight="fill" className="mr-2" />
          {emergency ? "URGENT — DOWNLOAD & START" : "DOWNLOAD & START"}
        </Button>
      </div>
      <div className="text-[10px] text-slate-500 mt-2 font-mono-num tracking-wider text-center">
        DOWNLOADING WILL MOVE THIS ORDER TO IN CUTTING
      </div>
    </div>
  );
}

/* ---------------- In-cutting card ---------------- */
function InCuttingCard({ o, busy, onDownload, onMarkDone, onPreview }) {
  const emergency = !!o.is_emergency;
  const cuttingBy = o.staff_name || o.cutting_started_by_name || "";
  const hasDesign = !!o.design_preview;
  return (
    <div
      className={
        "surface p-6 md:p-7 rounded-sm border-l-4 relative " +
        (emergency
          ? "border-red-500 ring-2 ring-red-500/50 shadow-lg shadow-red-500/20 bg-red-500/5"
          : "border-cyan-400")
      }
      data-testid={`operator-card-${o.order_number}`}
    >
      {emergency && <EmergencyPill />}
      {o.billing_mode === "job_work" && <div className="absolute -top-3 left-4 z-10"><JobWorkBadge small testid={`operator-job-work-${o.order_number}`} /></div>}
      {/* Prominent operator-attribution banner — tells the OTHER operator
          who has already picked up this cutting job so both know at a glance. */}
      {cuttingBy && (
        <div
          className={
            "mb-3 flex items-center gap-2 px-3 py-2 rounded-sm border " +
            (emergency
              ? "bg-red-500/15 border-red-500/60 text-red-200"
              : "bg-cyan-500/15 border-cyan-500/60 text-cyan-200")
          }
          data-testid={`cutting-by-${o.order_number}`}
        >
          <User size={16} weight="fill" />
          <span className="text-[11px] font-mono-num tracking-widest">CUTTING BY:</span>
          <span className="font-heading font-black tracking-wider text-white text-sm md:text-base uppercase truncate">
            {cuttingBy}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500">ORDER</div>
          <div className={
            "font-mono-num text-4xl font-black leading-none " +
            (emergency ? "text-red-300" : "text-cyan-300")
          }>
            #{o.order_number}
          </div>
          <div className="text-slate-300 mt-2 text-lg">{o.customer_name}</div>
        </div>
        <div className={
          "px-3 py-1 border rounded-sm flex items-center gap-2 " +
          (emergency ? "border-red-500 text-red-300" : "border-cyan-500 text-cyan-300")
        }>
          <Wrench size={14} weight="fill" className="animate-pulse" />
          <span className="text-[10px] font-mono-num tracking-widest">CUTTING…</span>
        </div>
      </div>
      <ItemsGrid o={o} />
      <div className="flex flex-col gap-3">
        {hasDesign && (
          <Button
            onClick={onPreview}
            variant="outline"
            className="w-full h-12 rounded-sm border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 font-heading tracking-widest"
            data-testid={`preview-design-${o.order_number}`}
          >
            <Eye size={18} weight="bold" className="mr-2" />
            PREVIEW DESIGN
          </Button>
        )}
        <Button
          onClick={onDownload}
          variant="outline"
          className="w-full h-12 rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800 font-heading tracking-widest"
          data-testid={`redownload-cutting-${o.order_number}`}
        >
          <DownloadSimple size={18} className="mr-2" />
          Re-download DXF
        </Button>
        <Button
          onClick={onMarkDone}
          disabled={busy}
          className="w-full h-20 rounded-sm text-2xl font-heading font-black tracking-widest text-white bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-colors"
          data-testid={`mark-done-${o.order_number}`}
        >
          <CheckCircle size={30} weight="fill" className="mr-3" />
          {busy ? "SAVING…" : "MARK CUTTING DONE"}
        </Button>
      </div>
    </div>
  );
}

function CardHeader({ o, label, tone = "orange" }) {
  const toneMap = {
    orange: { text: "text-orange-400", border: "border-orange-500" },
    cyan: { text: "text-cyan-300", border: "border-cyan-500" },
    red: { text: "text-red-300", border: "border-red-500" },
  };
  const t = toneMap[tone] || toneMap.orange;
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="text-[10px] font-mono-num tracking-widest text-slate-500">ORDER</div>
        <div className={`font-mono-num text-4xl font-black leading-none ${t.text}`}>
          #{o.order_number}
        </div>
        <div className="text-slate-300 mt-2 text-lg">{o.customer_name}</div>
      </div>
      <div className={`px-3 py-1 border rounded-sm ${t.border} ${t.text}`}>
        <span className="text-[10px] font-mono-num tracking-widest">{label}</span>
      </div>
    </div>
  );
}

/* Floating "🔥 EMERGENCY" pill for operator cards. Positioned top-right,
   pulsing to grab attention on a busy shop-floor screen. */
function EmergencyPill() {
  return (
    <div
      className="absolute -top-3 right-4 z-10 flex items-center gap-1.5 px-3 py-1 rounded-sm bg-red-500 text-white font-heading text-xs font-black tracking-widest shadow-lg shadow-red-500/50 animate-pulse"
      data-testid="emergency-pill"
    >
      <span>🔥</span> EMERGENCY
    </div>
  );
}

function ItemsGrid({ o }) {
  const lines = o.plate_flow && (o.cutting_plates || []).length ? o.cutting_plates : o.items;
  return (
    <div className="grid grid-cols-2 gap-2 mb-5">
      {o.plate_flow && (o.cutting_plates || []).length > 0 && (
        <div className="col-span-2 text-[10px] font-mono-num tracking-widest text-cyan-300" data-testid="operator-cutting-plates-label">// CUTTING SHEET SIZES (after framing) — gate size nahi</div>
      )}
      {lines.map((it, i) => {
        // Prefer RAW (as-fed) dimensions for cutting reference.
        // Fallback to `width_in` for old orders that don't have `_raw` fields yet.
        const wRaw = it.width_in_raw ?? it.width_in;
        const hRaw = it.height_in_raw ?? it.height_in;
        const hasMargin =
          it.width_in_raw != null && it.height_in_raw != null &&
          (it.width_in !== it.width_in_raw || it.height_in !== it.height_in_raw);
        return (
          <div key={i} className="surface-2 p-3 rounded-sm">
            <div className="flex items-center gap-1.5 text-[10px] font-mono-num text-slate-400 tracking-widest">
              <Package size={12} /> SHEET {i + 1}
            </div>
            <div className="font-mono-num text-xl text-white mt-1 font-bold">
              {it.thickness}
            </div>
            {it.item_name && (
              <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={it.item_name}>
                {it.item_name}
              </div>
            )}
            {/* REAL cutting size (what user fed) — primary */}
            <div className="flex items-center gap-1 text-base font-mono-num text-cyan-300 mt-1 font-bold">
              <Ruler size={14} />
              {formatDim(wRaw)}″ × {formatDim(hRaw)}″ × {it.quantity}
            </div>
            <div className="text-[9px] font-mono-num tracking-widest text-emerald-400/80 mt-0.5">
              // REAL SIZE — CUT THIS
            </div>
            {/* Optional: billed (margin) size shown small if it differs */}
            {hasMargin && (
              <div className="text-[10px] text-slate-500 mt-1">
                billed: {formatDim(it.width_in)}″ × {formatDim(it.height_in)}″
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatDim(v) {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

/* ---------------- Daily cutting chart ---------------- */
function DailyCuttingChart({ series, scope }) {
  const maxOrders = Math.max(1, ...series.map((s) => s.orders));
  const maxSqft = Math.max(1, ...series.map((s) => s.sqft));
  const totalOrders = series.reduce((a, s) => a + s.orders, 0);
  const totalSqft = series.reduce((a, s) => a + s.sqft, 0);
  const activeDays = series.filter((s) => s.orders > 0).length;

  return (
    <div className="surface rounded-sm p-4 md:p-5 mb-6 border border-slate-800" data-testid="operator-daily-chart">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono-num text-orange-400 tracking-widest">
            // DAILY CUTTING — LAST {series.length} DAYS {scope === "factory" ? "· FACTORY" : "· YOU"}
          </div>
          <div className="font-heading text-lg md:text-xl font-black text-white leading-tight mt-1">
            {totalOrders} orders · {totalSqft.toFixed(1)} sqft cut
            <span className="text-slate-500 text-sm font-normal ml-2">
              across {activeDays} day{activeDays === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px] font-mono-num tracking-widest">
          <span className="flex items-center gap-1.5 text-cyan-300">
            <span className="w-3 h-3 bg-cyan-400 inline-block rounded-sm" /> ORDERS
          </span>
          <span className="flex items-center gap-1.5 text-orange-300">
            <span className="w-3 h-3 bg-orange-500 inline-block rounded-sm" /> SQFT
          </span>
        </div>
      </div>

      {/* Bars */}
      <div className="flex items-end gap-1 md:gap-2 h-40 border-b border-slate-800 pb-1">
        {series.map((s) => {
          const orderH = (s.orders / maxOrders) * 100;
          const sqftH = (s.sqft / maxSqft) * 100;
          const hasData = s.orders > 0 || s.sqft > 0;
          return (
            <div key={s.date} className="flex-1 flex items-end justify-center gap-0.5 h-full relative group" title={`${s.label}: ${s.orders} orders, ${s.sqft} sqft`}>
              <div
                className={`w-1/2 rounded-t-sm transition-colors ${hasData ? "bg-cyan-500/80 group-hover:bg-cyan-400" : "bg-slate-800/40"}`}
                style={{ height: `${Math.max(orderH, hasData ? 4 : 0)}%` }}
              />
              <div
                className={`w-1/2 rounded-t-sm transition-colors ${hasData ? "bg-orange-500/80 group-hover:bg-orange-400" : "bg-slate-800/40"}`}
                style={{ height: `${Math.max(sqftH, hasData ? 4 : 0)}%` }}
              />
              {hasData && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 bg-slate-900 border border-slate-700 rounded-sm px-2 py-1 text-[10px] font-mono-num whitespace-nowrap">
                  <div className="text-cyan-300">{s.orders} orders</div>
                  <div className="text-orange-300">{s.sqft} sqft</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div className="flex gap-1 md:gap-2 mt-2">
        {series.map((s, i) => (
          <div key={s.date} className="flex-1 text-center text-[9px] font-mono-num text-slate-500 truncate">
            {(i % 2 === 0 || series.length <= 10) ? s.label : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatBox({ label, value, tone = "orange" }) {
  const t = {
    orange: "text-orange-400 border-orange-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    emerald: "text-emerald-400 border-emerald-500/40",
  }[tone];
  return (
    <div className={`surface p-4 rounded-sm border-t-2 ${t}`}>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400">{label.toUpperCase()}</div>
      <div className={`mt-1 font-heading text-3xl font-black font-mono-num ${t.split(" ")[0]}`}>{value}</div>
    </div>
  );
}
